import * as React from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Tiny toast system. We pair the primitive with a context provider so any
 * component can pop a toast via useToasts().
 */

const toastVariants = cva(
  "pointer-events-auto group relative flex w-full items-start gap-3 overflow-hidden rounded-md border p-3 pr-8 shadow-soft transition-all data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full",
  {
    variants: {
      tone: {
        default: "border-border bg-card text-card-foreground",
        success: "border-success/40 bg-success/15 text-success",
        warning: "border-warning/40 bg-warning/15 text-warning",
        danger: "border-destructive/50 bg-destructive/20 text-destructive-foreground",
        brand: "border-brand/40 bg-brand/15 text-brand",
      },
    },
    defaultVariants: { tone: "default" },
  },
);

export interface Toast extends VariantProps<typeof toastVariants> {
  id: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
  duration?: number;
}

interface ToastsCtx {
  toasts: Toast[];
  push: (t: Omit<Toast, "id">) => string;
  dismiss: (id: string) => void;
}

const Ctx = React.createContext<ToastsCtx | null>(null);

export function ToastsProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const push = React.useCallback((t: Omit<Toast, "id">) => {
    const id = crypto.randomUUID();
    setToasts((cur) => [...cur, { ...t, id }]);
    return id;
  }, []);
  const dismiss = React.useCallback(
    (id: string) => setToasts((cur) => cur.filter((t) => t.id !== id)),
    [],
  );
  return (
    <Ctx.Provider value={{ toasts, push, dismiss }}>
      <ToastPrimitive.Provider swipeDirection="right" duration={5000}>
        {children}
        {toasts.map((t) => (
          <ToastPrimitive.Root
            key={t.id}
            duration={t.duration ?? 5000}
            onOpenChange={(open) => {
              if (!open) dismiss(t.id);
            }}
            className={cn(toastVariants({ tone: t.tone }))}
          >
            <div className="flex-1">
              <ToastPrimitive.Title className="text-sm font-medium">
                {t.title}
              </ToastPrimitive.Title>
              {t.description ? (
                <ToastPrimitive.Description className="mt-0.5 text-xs opacity-80">
                  {t.description}
                </ToastPrimitive.Description>
              ) : null}
              {t.action ? <div className="mt-2 flex gap-1">{t.action}</div> : null}
            </div>
            <ToastPrimitive.Close className="absolute right-2 top-2 rounded p-1 opacity-70 hover:opacity-100">
              <X className="h-3.5 w-3.5" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed bottom-4 right-4 z-[100] flex w-96 max-w-[calc(100vw-2rem)] flex-col gap-2 outline-none" />
      </ToastPrimitive.Provider>
    </Ctx.Provider>
  );
}

export function useToasts(): ToastsCtx {
  const v = React.useContext(Ctx);
  if (!v) throw new Error("useToasts must be used within ToastsProvider");
  return v;
}
